import React, { useEffect, useRef, useState } from 'react';
import {
  PushState,
  enablePush,
  getPushState,
  markOptInDone,
  recordOptInDismissal,
  shouldShowOptIn,
} from '../../../utils/pushNotifications';
import './PushOptIn.scss';

/**
 * One-line, dismissible "get a notification when a client emails you" strip for
 * the mail feed. Renders NOTHING (so no dead space) unless push is actually
 * offerable: permission still undecided, or an iPhone that has to install the
 * app first - and not snoozed ("not now" hides it for 30 days).
 *
 * The [enable] handler calls enablePush() synchronously: the permission prompt
 * only counts inside a real tap.
 */

type Variant = 'terminal' | 'graphite' | 'classic';
type Phase = 'prompt' | 'busy' | 'done' | 'hidden';

const CONFIRM_MS = 6000;

interface PushOptInProps {
  /** terminal = [bracketed] actions, graphite/classic = plain text actions. */
  variant?: Variant;
}

const PushOptIn: React.FC<PushOptInProps> = ({ variant = 'terminal' }) => {
  const [initial] = useState<{ state: PushState; show: boolean }>(() => {
    const state = getPushState();
    return { state, show: shouldShowOptIn(state) };
  });
  const [state, setState] = useState<PushState>(initial.state);
  const [phase, setPhase] = useState<Phase>(initial.show ? 'prompt' : 'hidden');
  const [error, setError] = useState<string | null>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  // The one-line confirmation fades on its own; it is never shown again
  // because markOptInDone() ran before it appeared.
  useEffect(() => {
    if (phase !== 'done') return;
    const t = setTimeout(() => setPhase('hidden'), CONFIRM_MS);
    return () => clearTimeout(t);
  }, [phase]);

  if (phase === 'hidden') return null;

  const br = (s: string) => (variant === 'terminal' ? `[${s}]` : s);

  const dismiss = () => {
    recordOptInDismissal();
    setPhase('hidden');
  };

  const onEnable = () => {
    if (phase === 'busy') return;
    setPhase('busy');
    setError(null);
    // No await before this call - see the file header.
    void enablePush().then((res) => {
      if (!mounted.current) return;
      if (res.ok) {
        markOptInDone();
        setPhase('done');
        return;
      }
      if (res.reason === 'denied') setState('denied');
      setError(res.message);
      setPhase('prompt');
    });
  };

  const needsInstall = state === 'ios-needs-install';
  const blocked = state === 'denied';

  let text: string;
  if (phase === 'done') {
    text = "Notifications are on. You'll get a ping when a client emails you.";
  } else if (blocked) {
    text = error || 'Notifications are blocked for this site. Allow them in your browser settings to get client alerts.';
  } else if (needsInstall) {
    text = 'Get a notification when a client emails you: tap Share, then Add to Home Screen, and open the app from there.';
  } else {
    text = 'Get a notification when a client emails you';
  }

  return (
    <div
      className={`push-optin push-optin--${variant}${error && !blocked ? ' push-optin--error' : ''}`}
      role="region"
      aria-label="Email notifications"
    >
      <div className="push-optin__text" aria-live="polite">
        {text}
        {error && !blocked && phase !== 'done' && (
          <span className="push-optin__error"> ({error})</span>
        )}
      </div>
      {phase !== 'done' && (
        <div className="push-optin__actions">
          {!needsInstall && !blocked && (
            <button
              type="button"
              className="push-optin__btn push-optin__btn--accent"
              onClick={onEnable}
              disabled={phase === 'busy'}
            >
              {phase === 'busy' ? br('enabling...') : br(error ? 'try again' : 'enable')}
            </button>
          )}
          <button type="button" className="push-optin__btn" onClick={dismiss} disabled={phase === 'busy'}>
            {br(blocked ? 'ok' : 'not now')}
          </button>
        </div>
      )}
    </div>
  );
};

export default PushOptIn;
